<template>
  <v-dialog :value="visible" max-width="600px" @input="close">
    <v-card class="modal-card">
      <v-card-title class="headline-container">
        <span class="headline-text">{{ workflowLabel(getTitle) }}</span>
        <div class="headline-actions">
          <div v-if="hasMultipleOrders" class="order-navigation">
            <button
              type="button"
              class="order-navigation-button"
              :disabled="currentDetailsIndex === 0"
              :aria-label="adminText('workflowsAll.previous_order_f0fa5df', 'Previous order')"
              @click="showPreviousOrder"
            >
              <v-icon>chevron_left</v-icon>
            </button>
            <span class="order-navigation-label">
              <span class="order-navigation-title">{{ adminText('workflowsAll.order_1d75774', 'Order') }}</span>
              <strong>{{ workflowLabel(currentDetailsIndex + 1) }}</strong>
              <span class="order-navigation-total">/ {{ workflowLabel(seatDetailsList.length) }}</span>
            </span>
            <button
              type="button"
              class="order-navigation-button"
              :disabled="currentDetailsIndex === seatDetailsList.length - 1"
              :aria-label="adminText('workflowsAll.next_order_a295df1', 'Next order')"
              @click="showNextOrder"
            >
              <v-icon>chevron_right</v-icon>
            </button>
          </div>
          <v-icon class="close-icon" @click="close">close</v-icon>
        </div>
      </v-card-title>

      <v-card-text class="card-text">
        <div v-if="loading" class="details-skeleton">
          <div class="skeleton-box skeleton-title" />
          <div class="skeleton-box" />
          <div class="skeleton-box" />
          <div class="skeleton-box skeleton-row" />
          <div class="skeleton-box skeleton-row" />
        </div>
        <template v-else-if="showParterSummary">
          <div class="parter-summary-box">
            <span class="info-label">{{ adminText('workflowsAll.location_d219c68', 'Location') }}</span>
            <span class="info-value">{{ workflowLabel(isSpecialTableEventDisplay ? adminText('workflowsAll.parter_328f837', 'Parter') : parterSummary.ticketLocation) }}</span>
          </div>
          <div class="parter-summary-box">
            <span class="info-label">{{ adminText('workflowsAll.sold_tickets_9825f4d', 'Sold tickets') }}</span>
            <span class="info-value">{{ workflowLabel(parterSummary.totalSold) }}</span>
          </div>
          <div v-if="parterSummary.tickets && parterSummary.tickets.length" class="parter-ticket-list">
            <div v-for="ticket in parterSummary.tickets" :key="ticket.name" class="parter-ticket-row">
              <span class="parter-ticket-name">{{ workflowLabel(ticket.name) }}</span>
              <span class="parter-ticket-count">x {{ workflowLabel(ticket.count) }}</span>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="seat-info-container">
            <div v-if="showSectionBox" class="info-box">
              <span class="info-label">{{ adminText('workflowsAll.section_f2c6b56', 'Section') }}</span>
              <span class="info-value">{{ workflowLabel(getSection) }}</span>
            </div>
            <div v-if="showRowBox" class="info-box">
              <span class="info-label">{{ workflowLabel(rowInfoLabel) }}</span>
              <span class="info-value">{{ workflowLabel(getRow) }}</span>
            </div>
            <div v-if="showSeatBox" class="info-box">
              <span class="info-label">{{ workflowLabel(seatLabel) }}</span>
              <span class="info-value">{{ workflowLabel(getSeat) }}</span>
            </div>
            <div v-if="!isSpecialTableEventDisplay && showCapacity" class="info-box">
              <span class="info-label">{{ adminText('workflowsAll.capacity_45bd908', 'Capacity') }}</span>
              <span class="info-value">
                {{ workflowLabel(displayedSeatDetails.orderItem.itemMeta.min || 0) }} -
                {{ workflowLabel(displayedSeatDetails.orderItem.itemMeta.max || 10) }}
              </span>
            </div>
          </div>

          <div v-if="buyerInfo" class="buyer-info">
            <span v-if="!isParter" class="buyer-info-title">{{ adminText('workflowsAll.buyer_info_eb8824e', 'Buyer info') }}</span>
            <div v-if="!isParter" class="buyer-info-item">
              <v-icon class="buyer-info-icon">account_circle</v-icon>
              <span>{{ workflowLabel(buyerInfo['First name']) }} {{ workflowLabel(buyerInfo['Last name']) }}</span>
            </div>
            <div v-if="!isParter" class="buyer-info-item">
              <v-icon class="buyer-info-icon">mdi-email-outline</v-icon>
              <span>{{ workflowLabel(buyerInfo['Email']) }}</span>
            </div>
            <div class="buyer-info-item">
              <v-icon class="buyer-info-icon">mdi-ticket-confirmation-outline</v-icon>
              <div class="ticket-info-container">
                <span>{{ workflowLabel(displayedSeatDetails.orderItem.name) }}</span>
                <span v-if="displayedSeatDetails.numberOfSeats > 1">
                  x {{ workflowLabel(displayedSeatDetails.numberOfSeats) }}
                </span>
              </div>
            </div>
            <div v-if="!isParter" class="buyer-info-item">
              <v-icon class="buyer-info-icon">calendar_month</v-icon>
              <span>{{ workflowLabel(formattedDate) }}</span>
            </div>
          </div>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import moment from 'moment'
import i18next from 'i18next'

const TICKET_LOCATION_PREFIX = {
  SECTION: 'Section-',
  ROW: 'Row-',
  SEAT: 'Seat-',
}

export default {
  mixins: [WorkflowDisplay],
  name: 'seat-details-modal',
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    detailsType: {
      type: String,
      default: null,
    },
    seatDetails: {
      type: Object,
      default: () => ({}),
    },
    seatDetailsList: {
      type: Array,
      default: () => [],
    },
    parterSummary: {
      type: Object,
      default: null,
    },
    eventId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      currentDetailsIndex: 0,
    }
  },
  computed: {
    displayedSeatDetails() {
      return this.seatDetailsList[this.currentDetailsIndex] || this.seatDetails
    },
    normalizedItemMeta() {
      const itemMeta = this.displayedSeatDetails.orderItem.itemMeta || {}
      const parseBoolean = value => {
        if (value === true || value === 'true') return true
        if (value === false || value === 'false') return false

        return Boolean(value)
      }

      return {
        ...itemMeta,
        table: parseBoolean(itemMeta.table),
        parter: parseBoolean(itemMeta.parter),
      }
    },
    showParterSummary() {
      return Boolean(this.parterSummary)
    },
    isTable() {
      return this.normalizedItemMeta.table
    },
    isParter() {
      return this.normalizedItemMeta.parter
    },
    hasMultipleOrders() {
      return this.isTable && this.seatDetailsList.length > 1
    },
    isSpecialTableEventDisplay() {
      return [7345, 9022].includes(Number(this.eventId))
    },
    showSectionBox() {
      return this.getSection && (!this.isTable || this.isSpecialTableEventDisplay)
    },
    showRowBox() {
      return this.getRow && !this.isParter && (!this.isTable || this.isSpecialTableEventDisplay)
    },
    showSeatBox() {
      return this.getSeat && !this.isParter && !this.isSpecialTableEventDisplay
    },
    rowInfoLabel() {
      if (this.isSpecialTableEventDisplay) {
        const currentLanguage = i18next.language

        return this.isAdminLocalization ? this.adminText('workflowsAll.table_ecd31c8', 'Table') : currentLanguage === 'hr' ? 'Stol' : 'Table'
      }

      return this.adminText('workflowsAll.row_9bf7a8e', 'Row')
    },
    getTitle() {
      if (this.detailsType === 'table') {
        return this.adminText('workflowsAll.table_details_820ea2c', 'Table details')
      } else if (this.detailsType === 'parter') {
        return this.adminText('workflowsAll.parter_details_4ee9960', 'Parter details')
      } else if (this.detailsType === 'seat') {
        return this.adminText('workflowsAll.seat_details_a69b6fa', 'Seat details')
      } else if (this.isTable) {
        return this.adminText('workflowsAll.table_details_820ea2c', 'Table details')
      } else if (this.isParter) {
        return this.adminText('workflowsAll.parter_details_4ee9960', 'Parter details')
      }

      return this.adminText('workflowsAll.seat_details_a69b6fa', 'Seat details')
    },
    parsedTicketLocation() {
      if (!this.displayedSeatDetails.data.ticketLocation) return {}

      const [sectionPart, rowPart, seatPart] = this.displayedSeatDetails.data.ticketLocation.split('_')

      return {
        section: (sectionPart || '').replace(TICKET_LOCATION_PREFIX.SECTION, ''),
        row: (rowPart || '').replace(TICKET_LOCATION_PREFIX.ROW, ''),
        seat: (seatPart || '').replace(TICKET_LOCATION_PREFIX.SEAT, ''),
      }
    },
    getSection() {
      return this.parsedTicketLocation.section || null
    },
    getRow() {
      return this.parsedTicketLocation.row || null
    },
    getSeat() {
      return this.parsedTicketLocation.seat || null
    },
    seatLabel() {
      return this.isTable ? this.adminText('workflowsAll.customer_tableNumber', 'Table number') : this.adminText('workflowsAll.seat_b90b325', 'Seat')
    },
    showCapacity() {
      return this.normalizedItemMeta.min || this.normalizedItemMeta.max
    },
    buyerInfo() {
      return this.displayedSeatDetails.data.fields || null
    },
    formattedDate() {
      if (!this.displayedSeatDetails.data.createdAt) return ''

      return this.workflowDate(this.displayedSeatDetails.data.createdAt, 'DD.MM.YYYY. [at] HH:mm [h]')
    },
  },
  watch: {
    seatDetailsList() {
      this.currentDetailsIndex = 0
    },
  },
  methods: {
    showPreviousOrder() {
      if (this.currentDetailsIndex > 0) {
        this.currentDetailsIndex -= 1
      }
    },
    showNextOrder() {
      if (this.currentDetailsIndex < this.seatDetailsList.length - 1) {
        this.currentDetailsIndex += 1
      }
    },
    close() {
      this.$emit('close')
    },
  },
}
</script>

<style scoped>
.modal-card {
  border-radius: 0.5rem;
  background: #fff;
  box-shadow: 0 0.25rem 1rem 0 rgba(0, 0, 0, 0.1);
  padding: 1rem;
}

.headline-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0;
}

.headline-text {
  color: #0277bd;
  font-family: Raleway;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%; /* 24px */
  letter-spacing: -0.6px;
}

.close-icon {
  cursor: pointer;
  color: #666;
  transition: color 0.2s ease, transform 0.2s ease;
}

.close-icon:hover {
  color: #0277bd;
  transform: rotate(90deg);
}

.headline-actions,
.order-navigation {
  display: flex;
  align-items: center;
}

.headline-actions {
  gap: 0.75rem;
}

.order-navigation {
  gap: 0.375rem;
  padding: 0.25rem;
  border: 1px solid #9bcde2;
  border-radius: 999px;
  background: #e7f4fa;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9);
}

.order-navigation-button {
  display: inline-flex;
  width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid #b7dcea;
  border-radius: 50%;
  background: #fff;
  color: #005f8f;
  box-shadow: 0 1px 3px rgba(0, 95, 143, 0.18);
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.order-navigation-button:hover:not(:disabled),
.order-navigation-button:focus-visible {
  border-color: #006b9e;
  background: #006b9e;
  color: #fff;
  box-shadow: 0 0.25rem 0.625rem rgba(0, 95, 143, 0.3);
  outline: none;
  transform: translateY(-1px);
}

.order-navigation-button:disabled {
  border-color: #d2e3ea;
  background: #f8fcfd;
  color: #7e9aa7;
  box-shadow: none;
  cursor: default;
}

.order-navigation-button .v-icon {
  color: inherit;
  font-size: 1.25rem;
}

.order-navigation-label {
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
  min-width: 4.75rem;
  color: #0d4965;
  font-family: 'Source Sans Pro';
  font-size: 0.875rem;
  white-space: nowrap;
}

.order-navigation-title,
.order-navigation-total {
  color: #285f78;
}

.order-navigation-label strong {
  color: #005f8f;
  font-size: 1rem;
}

@media screen and (max-width: 420px) {
  .headline-container {
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  .headline-actions {
    width: 100%;
    justify-content: space-between;
  }

  .order-navigation {
    flex: 1;
    justify-content: center;
  }

  .order-navigation-button {
    width: 2.25rem;
    height: 2.25rem;
  }
}

.card-text {
  padding: 1rem 0 0 !important;
}

.seat-info-container {
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  justify-content: space-between;
}

.info-box {
  display: flex;
  flex-direction: column;
  padding: 1rem;
  align-items: flex-start;
  gap: 0.5rem;
  border-radius: 0.5rem;
  background: #f5f5f5;
  flex: 1;
}

.info-label {
  color: #666;
  font-family: 'Source Sans Pro';
  font-size: 1rem;
}

.info-value {
  color: #333;
  font-family: 'Source Sans Pro';
  font-size: 1.25rem;
  font-weight: 700;
}

.buyer-info {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.buyer-info-title {
  color: #0277bd;
  font-family: 'Source Sans Pro';
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
}

.ticket-info-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.buyer-info-item {
  display: flex;
  padding: 1rem;
  align-items: center;
  gap: 1rem;
  border-radius: 0.5rem;
  background: #f5f5f5;
  color: #333;
  font-family: 'Source Sans Pro';
  font-size: 1rem;
}

.buyer-info-icon {
  color: #666;
}

.parter-summary-box {
  display: flex;
  flex-direction: column;
  padding: 1rem;
  gap: 0.5rem;
  border-radius: 0.5rem;
  background: #f5f5f5;
}

.parter-summary-box + .parter-summary-box {
  margin-top: 0.75rem;
}

.parter-ticket-list {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.parter-ticket-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-radius: 0.5rem;
  background: #f5f5f5;
}

.parter-ticket-name {
  color: #333;
  font-family: 'Source Sans Pro';
  font-size: 1rem;
}

.parter-ticket-count {
  color: #333;
  font-family: 'Source Sans Pro';
  font-size: 1rem;
  font-weight: 700;
}

.details-skeleton {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.skeleton-box {
  border-radius: 0.5rem;
  background: linear-gradient(90deg, #f1f1f1 25%, #f7f7f7 37%, #f1f1f1 63%);
  background-size: 400% 100%;
  animation: skeleton-shimmer 1.4s ease infinite;
  height: 3rem;
}

.skeleton-title {
  height: 2.25rem;
  width: 60%;
}

.skeleton-row {
  height: 3.5rem;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}
</style>
